import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/AuthPage/AuthPage";
import MainScreen from "./pages/MainScreen/MainScreen";
import LoadingScreen from "./pages/LoadingScreen/LoadingScreen";
import toast, { Toaster } from "react-hot-toast";

import "./App.css";
import "./assets/fonts.css";
import VideoThumbnail from "react-video-thumbnail";
import { MenuProvider } from "./context/MenuContext";
import { Provider, useSelector } from "react-redux";
import { store } from "./store/store";
import "./i18n";
import VideoPage from "./components/VideoPage/VideoPage";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import GoogleOAuthCallback from "./pages/AuthPage/GoogleOAuthCallback";
import ProjectScreen from "./pages/ProjectScreen/ProjectScreen";
import AuthCallback from "./pages/AuthPage/AuthCallback";
import ProfileScreen from "./pages/ProfileScreen/ProfileScreen";
import SecurityScreen from "./pages/SecurityScreen/SecurityScreen";
import ActiveSessionsScreen from "./pages/ActiveSessionsScreen/ActiveSessionsScreen";
import ErrorScreen from "./pages/ErrorScreen/ErrorScreen";
import TeamScreen from "./pages/TeamScreen/TeamScreen";
import ShareAccessPage from "./pages/ShareAccessPage/ShareAccessPage";
import NotificationsScreen from "./pages/NotificationsScreen/NotificationsScreen";
import VersionUploadPopup from "./components/VersionUploadPopup/VersionUploadPopup";
import AdminScreen from "./pages/AdminScreen/AdminScreen";
import AdminOrganizationsScreen from "./pages/AdminOrganizationsScreen/AdminOrganizationsScreen";
import AdminStatsScreen from "./pages/AdminStatsScreen/AdminStatsScreen";
import TariffesScreen from "./pages/TariffesScreen/TariffesScreen";
import AdminReactionPacksScreen from "./pages/AdminReactionPacksScreen/AdminReactionPacksScreen";
import AdminReactionsScreen from "./pages/AdminReactionsScreen/AdminReactionsScreen";
import SupportScreen from "./pages/SupportScreen/SupportScreen";
import AgreementsScreen from "./pages/AgreementsScreen/AgreementsScreen";
import LegalDocumentScreen from "./pages/LegalDocumentScreen/LegalDocumentScreen";
import AcceptDocumentScreen from "./pages/AcceptDocumentScreen/AcceptDocumentScreen";
import HawkCatcher from "@hawk.so/javascript";
import CookieConsent from "react-cookie-consent";

const hawk = new HawkCatcher({
  consoleTracking: true,
  token:
    "eyJpbnRlZ3JhdGlvbklkIjoiYWU1MmNkNTktZDNkMy00ODMyLTkyNDYtNTlhOGI0OTE5MTYzIiwic2VjcmV0IjoiMjYzMGVjYjMtNjk4Mi00NTNjLWI5MDgtNTU0YjRhZWIyZTM5In0=",
});

const AppRoutes = () => {
  const { isAuthenticated, loading, initializing } = useAuth();

  if (loading || initializing) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/home" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          }
        />
        <Route path="/share/access" element={<ShareAccessPage />} />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <MainScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/video/:versionId"
          element={
            <ProtectedRoute>
              <VideoPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <TeamScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <ProtectedRoute>
              <TariffesScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <ProtectedRoute>
              <SecurityScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/active-sessions"
          element={
            <ProtectedRoute>
              <ActiveSessionsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/error/404"
          element={
            <ProtectedRoute>
              <ErrorScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/error/403"
          element={
            <ProtectedRoute>
              <ErrorScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/error/500"
          element={
            <ProtectedRoute>
              <ErrorScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/error/access"
          element={
            <ProtectedRoute>
              <ErrorScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/tariffes"
          element={
            <ProtectedRoute>
              <AdminScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/organizations"
          element={
            <ProtectedRoute>
              <AdminOrganizationsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/reactions"
          element={
            <ProtectedRoute>
              <AdminReactionsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/reaction-packs"
          element={
            <ProtectedRoute>
              <AdminReactionPacksScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminStatsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/agreements"
          element={
            <ProtectedRoute>
              <AgreementsScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/admin/agreements/:agreementId"
          element={
            <ProtectedRoute>
              <LegalDocumentScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/legal-documents"
          element={
            <ProtectedRoute>
              <AcceptDocumentScreen />
            </ProtectedRoute>
          }
        />
        <Route path="/oauth2/callback" element={<GoogleOAuthCallback />} />
        <Route path="/auth/success" element={<AuthCallback />} />
      </Routes>
      <VersionUploadPopup />
      <CookieConsent
        location="bottom"
        cookieName="cookie_consent"
        style={{
          background: "#0c1934",
          alignItems: "center",
          height: 80,
          left: 0,
          right: 0,
          borderRadius: 8,
          display: "flex",
          width: "fit-content",
          marginInline: "auto",
          marginBottom: 10,
        }}
        buttonStyle={{
          background: "rgb(52, 150, 255)",
          color: "white",
          fontSize: 14,
          borderRadius: 5,
          fontWeight: 600,
          padding: 10,
          marginLeft: 0,
        }}
        buttonId="accept_ym_cookie"
        containerStyle={{ bottom: 10 }}
        declineButtonStyle={{
          background: "#333b4b",
          color: "white",
          fontSize: 14,
          borderRadius: 5,
          fontWeight: 600,
          padding: 10,
        }}
        contentStyle={{ flex: 1, fontSize: 14 }}
        buttonText={"Принять"}
        expires={150}
        enableDeclineButton
        declineButtonText="Только необходимые"
      >
        Мы используем cookie, чтобы сделать сайт удобнее.
      </CookieConsent>
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <MenuProvider>
          <Router>
            <AppRoutes />
            <Toaster
              containerStyle={{
                zIndex: 110000, // For the container
              }}
              toastOptions={{
                style: {
                  zIndex: 110000, // For toasts
                },
              }}
            />
          </Router>
        </MenuProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
